/**
 * The build's environment, read once.
 *
 * `VITE_USE_EMULATOR` is a **dev-only** switch: a production build ignores it and always
 * talks to live Firebase, so a stray `.env` can never point a shipped Studio at localhost.
 */

import { STORAGE_BUCKET } from '@atlas-nook/brand';

/** The emulator ports, from `apps/studio/firebase/firebase.json` (not the defaults — see general/02). */
export const EMULATOR = {
  authUrl: 'http://127.0.0.1:9399',
  firestoreHost: '127.0.0.1',
  firestorePort: 8181,
  storageHost: '127.0.0.1',
  storagePort: 9199,
  functionsHost: '127.0.0.1',
  functionsPort: 5101,
} as const;

/**
 * Leave the Studio for the external checkout. One function so a test can stand in for the
 * navigation jsdom cannot make; nothing else in the app changes `location`.
 */
export function leaveStudio(url: string): void {
  window.location.assign(url);
}

export interface StudioEnv {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  useEmulator: boolean;
  /**
   * The external checkout the Studio hands an order to (PRD 06, "Checkout provider"). Absent
   * until a provider is chosen: the Order button then says so, in one line, and nothing is
   * sent anywhere. **No price, no payment UI, no stock live on-site** — the handoff is the
   * only payment-shaped thing in the product.
   */
}

function read(key: string): string {
  const value = import.meta.env[key];
  return typeof value === 'string' ? value : '';
}

/**
 * Against the emulator the web config is not checked, so the placeholders below are enough
 * to boot; a live build needs the real `VITE_FIREBASE_*` values (see `.env.example`).
 */
export function studioEnv(): StudioEnv {
  const useEmulator = import.meta.env.DEV && read('VITE_USE_EMULATOR') === 'true';

  return {
    apiKey: read('VITE_FIREBASE_API_KEY') || (useEmulator ? 'emulator' : ''),
    authDomain: read('VITE_FIREBASE_AUTH_DOMAIN') || (useEmulator ? 'localhost' : ''),
    projectId: read('VITE_FIREBASE_PROJECT_ID') || (useEmulator ? 'atlas-nook' : ''),
    // Named rather than inferred on the emulator: the backend writes to the project's real
    // bucket (`@atlas-nook/brand`), so the browser has to read from the same one.
    storageBucket: read('VITE_FIREBASE_STORAGE_BUCKET') || (useEmulator ? STORAGE_BUCKET : ''),
    messagingSenderId: read('VITE_FIREBASE_MESSAGING_SENDER_ID'),
    appId: read('VITE_FIREBASE_APP_ID') || (useEmulator ? 'studio-emulator' : ''),
    useEmulator,
    // Named as the repo names its own config (`ATLAS_NOOK_` prefix, CLAUDE.md); `vite.config`
    // exposes exactly this one variable and no other `ATLAS_NOOK_*` to the bundle.
  };
}
